import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Envs, type FCWithChildren, UserLanguage } from "../@types";
import {
  getEnvironment,
  getSubdomainForEnvironment,
} from "../helpers/urlHelpers";
import { getGlobalHeaderAssetEntries } from "../helpers/maxGlobalHeaderHelpers";
import { getLoginUrl, hasLocalSession } from "../helpers/loginHelpers";
import { shouldDisplayGlobalHeader } from "../helpers/routeHelpers";
import { useIsCsrSession } from "../helpers/csrHelpers";
import { hideGlobalHeaderPaths } from "../app/routes";
import { useLocation } from "react-router-dom";
import { getBeneFirstAndLastName } from "../helpers/beneficiaryInfoHelpers";
import { useTypedTranslate } from "../helpers/intlHooks";
import {
  config,
  MCT_APP_MAIN_ID,
  MCT_GLOBAL_SKIP_NAV_ID,
  MCT_ROOT_ID,
} from "../config";
import { useAppContext } from "../helpers/context-hooks/useAppContext";
import { setSubdomain } from "../helpers/urlHelpers";
import { updateGlobalSkipNav } from "../helpers/skipNavHelpers";

export const consistentHeaderTestId = "ch-root";

// Adds a JS file to the document head
function addJsFileToHead(url: string): void {
  const jsElement = document.createElement("script");
  jsElement.src = url;
  jsElement.type = "text/javascript";
  jsElement.dataset.chAsset = "true";

  const head = document.head;
  head.insertBefore(jsElement, head.childNodes[0]);
}

// Adds a CSS file to the document head
function addCssFileToHead(url: string): void {
  const cssElement = document.createElement("link");
  cssElement.href = url;
  cssElement.rel = "stylesheet";
  cssElement.type = "text/css";
  cssElement.dataset.chAsset = "true";

  const head = document.head;
  head.insertBefore(cssElement, head.childNodes[0]);
}

export const MaxGlobalHeader: FCWithChildren = ({ children }) => {
  const location = useLocation();
  // Determine whether the global (consistent) header should be displayed,
  // based on the current route - Also see `/components/Header.tsx`
  const displayGlobalHeader = shouldDisplayGlobalHeader(
    location,
    hideGlobalHeaderPaths
  );
  const { feEnableChat, feEnableAuthentication } = useFlags();
  const { state } = useAppContext();
  const isCsr = useIsCsrSession();
  const t = useTypedTranslate();
  const GLOBAL_HEADER_ID = "root";

  // * State
  const [appRootEl, setAppRootEl] = useState<HTMLDivElement | null>(null);
  const [appMainEl, setAppMainEl] = useState<HTMLDivElement | null>(null);

  // Takes the provided array, parses it and loads all of the JS and CSS files based on their file extension
  const importFilesFromArray = useCallback(
    (files: string[], rootUrl: string): void => {
      const cssFiles = files.filter(file => file.endsWith(".css"));
      const jsFiles = files.filter(file => file.endsWith(".js"));

      // The order matters here: CSS files should be loaded first
      for (const file of cssFiles) addCssFileToHead(rootUrl + file);
      for (const file of jsFiles) addJsFileToHead(rootUrl + file);
    },
    []
  );

  // Gets and parses the entrypoints node of the asset manifest file
  const loadFilesFromManifest = useCallback(
    (rootUrl: string, manifest: string): void => {
      const manifestUrl = rootUrl + manifest;

      getGlobalHeaderAssetEntries(manifestUrl).then(entrypoints =>
        importFilesFromArray(entrypoints, rootUrl)
      );
    },
    [importFilesFromArray]
  );

  const setHeaderProperties = useCallback(
    (globalHeader: HTMLDivElement): void => {
      const oppositeLanguage =
        state.language === UserLanguage.English ? "es" : "en";
      let langToggleLink = window.location.href.replace(
        /lang=(en|es)/gim,
        `lang=${oppositeLanguage}`
      );
      const env = getEnvironment();
      if (env !== Envs.local) {
        langToggleLink = setSubdomain({
          subdomain: getSubdomainForEnvironment(),
          url: langToggleLink,
        });
      }
      const lang = state.language;
      const loginUrl = getLoginUrl(state.language);
      const logoutUrl = `${window.location.origin}${window.location.pathname}#/logout`;
      const hasSession = hasLocalSession();
      const hasBene = !!state.beneficiary;
      const loggedIn = hasSession && hasBene;
      const headerType = isCsr && loggedIn ? "CSR" : "MAX";
      const currentViewPage = `MCT-${location.pathname.replace("/", "")}`;

      globalHeader.setAttribute("lang", lang);
      globalHeader.setAttribute("lang-toggle-link", langToggleLink);
      globalHeader.setAttribute("login-url", loginUrl);
      globalHeader.setAttribute("logout-url", logoutUrl);
      globalHeader.setAttribute(
        "enable-live-chat",
        Boolean(feEnableChat).toString()
      );
      globalHeader.setAttribute("live-chat-viewpage", currentViewPage);
      globalHeader.setAttribute("header-type", headerType);
      globalHeader.setAttribute(
        "disable-login",
        Boolean(!feEnableAuthentication).toString()
      );
      globalHeader.dataset.testid = consistentHeaderTestId;

      if (state.beneficiary) {
        globalHeader.setAttribute(
          "beneficiary",
          getBeneFirstAndLastName(state.beneficiary, t)
        );
      }
    },
    [
      feEnableAuthentication,
      feEnableChat,
      location.pathname,
      state.beneficiary,
      isCsr,
      state.language,
      t,
    ]
  );

  const createGlobalHeader = useCallback((): void => {
    const globalHeader = document.createElement("div");
    globalHeader.id = GLOBAL_HEADER_ID;

    setHeaderProperties(globalHeader);

    const appRoot = document.getElementById(`${MCT_ROOT_ID}`);

    if (appRoot) {
      setAppRootEl(appRoot as HTMLDivElement);
    }

    // Add the header as the first child of the app div
    appRoot?.prepend(globalHeader);

    const env = getEnvironment();

    const manifestUrls = {
      [Envs.local]: "https://frontend.test.medicare.gov/",
      [Envs.test]: "https://frontend.test.medicare.gov/",
      [Envs.dev]: "https://frontend.dev.medicare.gov/",
      [Envs.imp]: "https://frontend.imp.medicare.gov/",
      [Envs.prod]: "https://frontend.medicare.gov/",
    };

    const manifestUrl = manifestUrls[env];

    loadFilesFromManifest(manifestUrl, "asset-manifest.json");
  }, [loadFilesFromManifest, setHeaderProperties]);

  // When any of the global header properties change, we must set the new values on the element
  useEffect(() => {
    let chMenuObserver: MutationObserver | void | undefined;
    (async () => {
      if (!displayGlobalHeader) {
        return;
      }
      const globalHeader = document.getElementById(GLOBAL_HEADER_ID);
      // Header is on, but no header exist in the DOM, create it
      if (globalHeader) {
        // Header is on and header already exists so update the properties
        setHeaderProperties(globalHeader as HTMLDivElement);
      } else {
        createGlobalHeader();
      }
      if (state.beneficiary && config.DEBUG) {
        const { initializeLocalLogouts } = await import(
          "./helpers/initializeLocalLogouts"
        );
        chMenuObserver = initializeLocalLogouts();
      }
    })();
    return () => {
      chMenuObserver?.disconnect();
    };
  }, [
    createGlobalHeader,
    displayGlobalHeader,
    feEnableAuthentication,
    feEnableChat,
    location,
    setHeaderProperties,
    state.beneficiary,
    state.language,
  ]);

  /** ----- Global skip nav setup & effects ----- */
  /**
   * Set a reference to the global skip nav (in the DOM) in component state
   * This reference is used by the hook that updates the nav when the route changes
   * The skip nav is in the HTML template, so should 100% be defined when this code runs
   * Query it once here, where it can be referenced in an effect
   */
  const globalSkipNavEl = useMemo(
    () =>
      document.getElementById(
        `${MCT_GLOBAL_SKIP_NAV_ID}`
      ) as HTMLAnchorElement | null,
    []
  );

  /**
   * Query for the MCT `main` element, if it exists at the time this code runs
   * The `main` will be used as the primary target for the global skip nav
   */
  const initialAppMainEl = useMemo(
    () =>
      document.getElementById(`${MCT_APP_MAIN_ID}`) as HTMLDivElement | null,
    []
  );

  /**
   * Update the global skip nav on every route change
   * This has nothing directly to do with the global header, but is inserted in
   * the DOM directly above it. Without creating a new component, this seems the best
   * place to handle
   * @see `public/index.html` (where the original nav is added to HTML)
   * @see `/src/helpers/skipNavHelpers.ts` (includes a hook for custom skip nav updates)
   */
  useEffect(() => {
    if (!globalSkipNavEl || !appMainEl) {
      return;
    }
    updateGlobalSkipNav({
      skipNavEl: globalSkipNavEl,
      location,
      textContent: t("skip_to_content"),
    });
  }, [appMainEl, globalSkipNavEl, location, t]);

  /**
   * Watch the MCT "root" element (defined in our HTML template) for the MCT `main`
   * element to be added to the DOM, if it doesn't exist before this effect runs.
   * The reference to this element is saved in component state.
   */
  useEffect(() => {
    if (!appRootEl) {
      return;
    }
    if (initialAppMainEl) {
      setAppMainEl(initialAppMainEl);
      return;
    }
    const observerCb: MutationCallback = mutations => {
      for (const mutationRecord of mutations) {
        if (mutationRecord.type === "childList") {
          for (const node of Array.from(mutationRecord.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if ((node as HTMLElement).id === `${MCT_APP_MAIN_ID}`) {
                setAppMainEl(node as HTMLDivElement);
                break;
              } else {
                const queriedAppMainEl = (node as HTMLElement).querySelector(
                  `#${MCT_APP_MAIN_ID}`
                );
                if (queriedAppMainEl) {
                  setAppMainEl(queriedAppMainEl as HTMLDivElement);
                  break;
                }
              }
            }
          }
        }
      }
    };
    const mo = new MutationObserver(observerCb);
    mo.observe(appRootEl, {
      childList: true,
      subtree: true,
    });
    return () => {
      mo.disconnect();
    };
  }, [appRootEl, initialAppMainEl]);

  return <>{children}</>;
};
